import React, { useMemo, useState } from 'react';
import { Check, Copy, Plus, UserRound, Users, X } from 'lucide-react';
import { Avatar, Card, hours, Label } from './ui';
import { projectRoles, roleMembers, roleOptions } from './team';
import { RESERVED_DOMAIN_BY_KEY, reservedDomainLabel } from '../Holacracy/reservedDomains';

// Équipe (rôles qui visent le projet) et contacts du client, avec ajout et retrait sur place.

const AddMember = ({ roles, employees, onAdd, onClose }) => {
  const [roleId, setRoleId] = useState(roles[0]?.id || '');
  const [employeeId, setEmployeeId] = useState('');
  const role = roles.find((r) => String(r.id) === String(roleId));
  const people = role ? roleMembers(role, employees) : [];
  return (
    <div className="mt-2 space-y-2 rounded-xl bg-slate-50 p-2.5">
      <div className="flex gap-1.5">
        <select value={roleId} onChange={(e) => { setRoleId(e.target.value); setEmployeeId(''); }} aria-label="Rôle"
          className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs">
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} aria-label="Personne"
          className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs">
          <option value="">{people.length ? 'Qui ?' : 'Personne dans ce rôle'}</option>
          {people.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      </div>
      <div className="flex justify-end gap-1">
        <button type="button" onClick={onClose} className="rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-white">Annuler</button>
        <button type="button" disabled={!employeeId} onClick={() => onAdd(roleId, employeeId).then(onClose, () => {})}
          className="rounded-md bg-slate-900 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-30">Ajouter</button>
      </div>
    </div>
  );
};

export const TeamCard = ({ project, focus, circles, employees, parentCircle, overview, actions }) => {
  const [adding, setAdding] = useState(false);
  const roles = useMemo(() => projectRoles(project.id, focus, circles, employees), [project.id, focus, circles, employees]);
  const options = useMemo(() => roleOptions(parentCircle, circles), [parentCircle, circles]);
  const people = overview?.hours?.people || [];
  const max = Math.max(1, ...people.map((p) => p.hours));
  return (
    <Card title="Équipe" icon={Users} action={options.length > 0 && (
      <button type="button" onClick={() => setAdding((v) => !v)} title="Ajouter quelqu'un" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
        <Plus size={15} />
      </button>
    )}>
      <ul className="space-y-2">
        {roles.map((role) => (
          <li key={role.roleId}>
            <p className="mb-1 flex flex-wrap items-center gap-1 text-[11px] font-medium text-slate-400">
              {role.role}
              {(role.reserved || []).map((key) => (
                <span key={key} title={RESERVED_DOMAIN_BY_KEY[key]?.effect}
                  className="rounded-full bg-violet-50 px-1.5 py-px text-[10px] font-medium text-violet-700">
                  {reservedDomainLabel(key)}
                </span>
              ))}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {role.members.map((member) => (
                <span key={member.id} className="group flex items-center gap-1.5 rounded-full bg-slate-50 py-0.5 pl-0.5 pr-2 text-sm text-slate-700">
                  <Avatar name={member.name} picture={member.picture} size="h-6 w-6" />
                  {member.name}
                  <button type="button" title={`Retirer ${member.name} de ce rôle sur le projet`}
                    onClick={() => window.confirm(`Retirer ${member.name} (${role.role}) du projet ?`) && actions.setMember(role.roleId, member.id, false).catch(() => {})}
                    className="-mr-1 hidden rounded-full p-0.5 text-slate-400 hover:bg-white hover:text-rose-600 group-hover:block">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </li>
        ))}
        {!roles.length && <p className="text-sm text-slate-400">Personne n'a encore de rôle sur ce projet.</p>}
      </ul>
      {adding && <AddMember roles={options} employees={employees} onClose={() => setAdding(false)}
        onAdd={(roleId, employeeId) => actions.setMember(roleId, employeeId, true)} />}
      {(people.length > 0 || overview?.team_today?.length > 0) && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          {people.length > 0 && (
            <>
              <Label className="mb-1.5">Heures sur 4 semaines · {hours(overview.hours.total)}</Label>
              <ul className="space-y-1">
                {people.slice(0, 5).map((person) => (
                  <li key={person.id} className="flex items-center gap-2 text-xs">
                    <span className="w-20 shrink-0 truncate text-slate-600">{person.name}</span>
                    <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <span className="block h-full rounded-full bg-indigo-400" style={{ width: `${(person.hours / max) * 100}%` }} />
                    </span>
                    <span className="w-10 shrink-0 text-right tabular-nums text-slate-500">{hours(person.hours)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          {overview?.team_today?.length > 0 && (
            <p className="mt-2 text-[11px] text-slate-500">Dessus aujourd'hui : {overview.team_today.join(', ')}</p>
          )}
        </div>
      )}
    </Card>
  );
};

const Contact = ({ contact, onRole, onRemove }) => {
  const [editing, setEditing] = useState(false);
  const [role, setRole] = useState(contact.role_in_project || '');
  const [copied, setCopied] = useState(false);
  const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.email;
  const copy = () => {
    navigator.clipboard?.writeText(contact.email).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {});
  };
  return (
    <li className="group flex items-start gap-2.5">
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400"><UserRound size={14} /></span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-slate-800" title={contact.comment || undefined}>{name}</p>
        {editing ? (
          <input autoFocus value={role} onChange={(e) => setRole(e.target.value)} placeholder="Rôle sur le projet"
            onBlur={() => { setEditing(false); if (role !== (contact.role_in_project || '')) onRole(role).catch(() => {}); }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setRole(contact.role_in_project || ''); setEditing(false); } }}
            className="mt-0.5 w-full rounded-md border border-slate-200 px-1.5 py-0.5 text-xs" />
        ) : (
          <button type="button" onClick={() => setEditing(true)} className="block max-w-full truncate text-left text-[11px] text-slate-400 hover:text-slate-700">
            {contact.role_in_project || contact.job_title || 'Rôle à préciser'}
          </button>
        )}
      </div>
      <div className="flex shrink-0 items-center opacity-0 transition group-hover:opacity-100">
        {contact.email && (
          <button type="button" onClick={copy} title={`Copier ${contact.email}`} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
          </button>
        )}
        <button type="button" onClick={() => window.confirm(`Retirer ${name} des contacts du projet ?`) && onRemove().catch(() => {})}
          title="Retirer du projet" className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><X size={13} /></button>
      </div>
    </li>
  );
};

export const ClientCard = ({ project, clients, actions }) => {
  const [adding, setAdding] = useState(false);
  const client = clients.find((c) => Number(c.id) === Number(project.client_id));
  const assigned = project.client_contacts || [];
  const available = (client?.contacts || []).filter((c) => !assigned.some((a) => Number(a.contact_id) === Number(c.id)));
  if (!project.client_id) return null;
  return (
    <Card title={project.client_name || 'Client'} icon={UserRound} action={available.length > 0 && (
      <button type="button" onClick={() => setAdding((v) => !v)} title="Ajouter un contact" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
        <Plus size={15} />
      </button>
    )}>
      {assigned.length ? (
        <ul className="space-y-2">
          {assigned.map((contact) => (
            <Contact key={contact.contact_id} contact={contact}
              onRole={(role) => actions.updateContactRole(contact.contact_id, role)}
              onRemove={() => actions.removeContact(contact.contact_id)} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-400">Aucun contact rattaché au projet.</p>
      )}
      {adding && (
        <select autoFocus defaultValue="" aria-label="Contact à ajouter"
          onChange={(e) => { if (e.target.value) actions.addContact(Number(e.target.value), '').then(() => setAdding(false), () => {}); }}
          className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm">
          <option value="">Choisir un contact…</option>
          {available.map((c) => <option key={c.id} value={c.id}>{[c.first_name, c.last_name].filter(Boolean).join(' ') || c.email}{c.job_title ? ` · ${c.job_title}` : ''}</option>)}
        </select>
      )}
    </Card>
  );
};
